import React from 'react';

import { Modal } from '../../../../common/display/modal/Modal';
import { Benefit } from '../../../../../@types/benefits';
import type { BenefitModalProps } from '../../../../../@types/benefitModalProps';
import { APP_CONFIG } from '../../../../../config';
import { BuyerPlusLogo } from '../../../../common/display/buyerPlusLogo/BuyerPlusLogo';
import { OrderedList } from '../../../../common/display/orderedList/OrderedList';
import { ImageSource } from '../../../../common/display/imageSource/ImageSource';

import RealEstateDamageReportJpg from './real-estate-damage-report.jpg';
import RealEstateDamageReportWebp from './real-estate-damage-report.webp';
import RealEstateDamageReportMobileJpg from './real-estate-damage-report-mobile.jpg';
import RealEstateDamageReportMobileWebp from './real-estate-damage-report-mobile.webp';
import DocumentsAndPenPng from './documents-and-pen.png';
import DocumentsAndPenWebp from './documents-and-pen.webp';
import CalendarWithCheckmarkPng from './calendar-with-checkmark.png';
import CalendarWithCheckmarkWebp from './calendar-with-checkmark.webp';
import ManInSuitPng from './man-in-suit.png';
import ManInSuitWebp from './man-in-suit.webp';
import { FAQList } from '../../../../common/display/faqTab/faqList/FAQList';
import { CustomerService } from '../../../../common/display/faqTab/customerService/CustomerService';
import { Msg, useIntl } from '@/i18n';

const styles = {
  overviewTab: { maxWidth: 530 },
};

const OverviewTab = () => (
  <div style={styles.overviewTab}>
    <div className='font-heading-xsmall-regular padding-bottom-l'>
      <Msg
        id='benefits.realEstateDamageReport.modal.overview.subHeading'
        defaultMessage='Report & recommendation to fix damages'
      />
    </div>
    <div className='padding-bottom-l'>
      <Msg
        id='benefits.realEstateDamageReport.modal.overview.paragraph'
        defaultMessage='If damage happens fast & diligent acting is required'
      />
    </div>
    <ul className='list-check list-spacing-s'>
      <li>
        <Msg
          id='benefits.realEstateDamageReport.modal.overview.bullet1'
          defaultMessage='15% discount for KäuferPlus'
          values={{ BuyerPlusLogo: <BuyerPlusLogo /> }}
        />
      </li>
      <li>
        <Msg
          id='benefits.realEstateDamageReport.modal.overview.bullet2'
          defaultMessage='On-site inspections & report'
        />
      </li>
      <li>
        <Msg
          id='benefits.realEstateDamageReport.modal.overview.bullet3'
          defaultMessage='Evaluation of cause and financial scope'
        />
      </li>
      <li>
        <Msg id='benefits.realEstateDamageReport.modal.overview.bullet4' defaultMessage='Listing of needed repairs' />
      </li>
      <li>
        <Msg id='benefits.realEstateDamageReport.modal.overview.bullet5' defaultMessage='Optional cost estimate' />
      </li>
    </ul>
  </div>
);

const HowItWorksTab = () => {
  const { t } = useIntl();
  const Item = OrderedList.Item;

  return (
    <div className='margin-bottom-xxl'>
      <OrderedList>
        <Item
          image={
            <ImageSource
              image={DocumentsAndPenPng}
              imageWebp={DocumentsAndPenWebp}
              imageAlt={t({
                id: 'benefits.realEstateDamageReport.modal.howItWorks.image1',
                defaultMessage: 'Two documents and a pen',
              })}
              sizes='100px'
            />
          }
          text={t({
            id: 'benefits.realEstateDamageReport.modal.howItWorks.paragraph1',
            defaultMessage: 'You found a damage...',
          })}
        />
        <Item
          image={
            <ImageSource
              image={CalendarWithCheckmarkPng}
              imageWebp={CalendarWithCheckmarkWebp}
              imageAlt={t({
                id: 'benefits.realEstateDamageReport.modal.howItWorks.image2',
                defaultMessage: 'Calendar with checkmark',
              })}
              sizes='100px'
            />
          }
          text={t({
            id: 'benefits.realEstateDamageReport.modal.howItWorks.paragraph2',
            defaultMessage: 'Within a couple of days you will get...',
          })}
        />
        <Item
          image={
            <ImageSource
              image={ManInSuitPng}
              imageWebp={ManInSuitWebp}
              imageAlt={t({
                id: 'benefits.realEstateDamageReport.modal.howItWorks.image3',
                defaultMessage: 'Man in suit',
              })}
              sizes='100px'
            />
          }
          text={t({
            id: 'benefits.realEstateDamageReport.modal.howItWorks.paragraph3',
            defaultMessage: 'In a personal meeting...',
          })}
        />
      </OrderedList>
    </div>
  );
};

const Faq = () => (
  <div>
    <div className='font-heading-xsmall-regular padding-bottom-l'>
      <div role='heading' aria-level={5}>
        <Msg id='benefits.realEstateDamageReport.modal.faqHeadline1' defaultMessage='General use-cases' />
      </div>
    </div>
    <FAQList benefit='realEstateDamageReport' start={0} end={4} />
    <div className='font-heading-xsmall-regular padding-bottom-l'>
      <div role='heading' aria-level={5}>
        <Msg id='benefits.realEstateDamageReport.modal.faqHeadline2' defaultMessage='Costs & procedure' />
      </div>
    </div>
    <FAQList benefit='realEstateDamageReport' start={5} end={11} />
    <CustomerService />
  </div>
);

const buttonArea = (
  <div className='align-center'>
    <a
      className='button-secondary button-rounded'
      href={`${APP_CONFIG.buyingPageUrl}?modal=realEstateDamageReport&cmp_id=10-051669&cmp_name=myscout_buying_buy&cmp_position=residential_benefits_buy&cmp_creative=damage_report_cta`}
    >
      <Msg id='benefits.realEstateDamageReport.modal.linkButton' defaultMessage='Request offer' />
    </a>
  </div>
);

export const RealEstateDamageReport = ({ isVisible, onClose }: BenefitModalProps) => {
  const { t } = useIntl();

  return (
    <Modal
      title={t({ id: 'benefits.realEstateDamageReport.headline', defaultMessage: 'DEKRA Real estate damage report' })}
      isVisible={isVisible}
      image={RealEstateDamageReportJpg}
      imageWebp={RealEstateDamageReportWebp}
      imageMobile={RealEstateDamageReportMobileJpg}
      imageMobileWebp={RealEstateDamageReportMobileWebp}
      imageUseFullWidth={true}
      imageAlt={t({
        id: 'benefits.realEstateDamageReport.modal.image',
        defaultMessage: 'Man looking at plan',
      })}
      tabsContent={[<OverviewTab key='overview' />, <HowItWorksTab key='howItWorks' />, <Faq key='faq' />]}
      buttonArea={buttonArea}
      benefit={Benefit.REAL_ESTATE_DAMAGE_REPORT}
      onClose={onClose}
    />
  );
};
